import { useMemo, useState } from "react";
import {
  CalendarCheck,
  ArrowsInSimple,
  ArrowsOutSimple,
} from "@phosphor-icons/react";
import { formatTime } from "../../utils/dateUtils";
import {
  useCalendarColorMode,
  resolveAppointmentColor,
  TYPE_FALLBACK_COLOR,
} from "../../utils/appointmentDisplay";
import "./CalendarSummaryOverlay.css";

// Single-line color legend — mirrors the appointment colors used by the
// calendar views (see AppointmentCard.css).
const LEGEND_ITEMS = [
  { color: "var(--color-primary)", label: "מאושרת" },
  { color: "#facc15", label: "ממתין לאישור" },
  { color: "#cc0000", label: "בוטל / לא אישר" },
  { color: "#f472b6", label: "ממתין לעדכון" },
  { color: "#60a5fa", label: "טופל במלואו" },
  { color: "var(--color-blocked)", label: "זמן חסום" },
];

// Per-meeting status dot color — mirrors AppointmentCard.getStatusInfo()
// exactly, so the dot always matches the meeting's color on the calendar.
const getStatusColor = (apt) => {
  if (apt.isBlockedTime === true) return "var(--color-blocked)";
  if (apt.status === "pending_approval") return "#f59e0b"; // amber
  if (apt.status === "cancelled") return "#cc0000"; // red

  const endMs = apt.endTime ? new Date(apt.endTime).getTime() : NaN;
  const isEnded =
    apt.status === "completed" || (!Number.isNaN(endMs) && endMs < Date.now());
  if (isEnded) {
    const fullyHandled =
      apt.isApprovedByTherapist === true &&
      apt.isTherapistSummarizedTheMeeting === true &&
      apt.isTherapistUpdatedPaymentStatus === true;
    return fullyHandled ? "#60a5fa" : "#f472b6"; // blue / pending update
  }

  if (apt.whatsappInitialDeclined === true || apt.whatsappReminderDeclined === true)
    return "#cc0000"; // red — declined
  if (apt.whatsappInitialConfirmed === true || apt.whatsappReminderConfirmed === true)
    return "var(--color-primary)"; // turquoise — confirmed
  // No SMS requested / no SMS activity — treated as confirmed on the calendar
  if (apt.initialSmsEnabled === false && apt.reminderSmsEnabled === false)
    return "var(--color-primary)";
  if (
    !apt.whatsappInitialSent &&
    !apt.whatsappReminderSent &&
    !apt.initialSmsScheduledAt &&
    !apt.reminderSmsScheduledAt
  )
    return "var(--color-primary)";

  return "var(--color-warning)"; // yellow — waiting for response
};

/**
 * Floating glass summary bar overlaid on the bottom of the calendar.
 * Today's remaining meetings + this-week / next-week counters, with a
 * one-line status color legend.
 */
function CalendarSummaryOverlay({
  todayAppointments = [],
  upcomingAppointments = [],
  onAppointmentClick,
}) {
  // Clinic color mode — in type mode dots + legend follow treatment types.
  // Local getStatusColor() stays as-is for status mode (pixel-identical).
  const { mode: colorMode, treatmentTypeMap, treatmentTypes } = useCalendarColorMode();
  const isTypeMode = colorMode === "treatmentType";
  const dotColor = (apt) => {
    if (!isTypeMode) return getStatusColor(apt);
    const info = resolveAppointmentColor({ appointment: apt, mode: colorMode, treatmentTypeMap });
    return info.kind === "type" ? info.hex : getStatusColor(apt);
  };
  const legendItems = isTypeMode
    ? [
        ...treatmentTypes.map((t) => ({
          color: t.color || TYPE_FALLBACK_COLOR,
          label: t.name || "סוג טיפול",
        })),
        { color: TYPE_FALLBACK_COLOR, label: "ללא סוג טיפול" },
        { color: "#cc0000", label: "בוטל" },
        { color: "var(--color-blocked)", label: "זמן חסום" },
      ]
    : LEGEND_ITEMS;
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Blocked time (חסימת זמן) is not a meeting — excluded from every counter
  const todayMeetings = useMemo(
    () => todayAppointments.filter((apt) => apt.isBlockedTime !== true),
    [todayAppointments],
  );

  // Remaining (future, non-cancelled) meetings today, soonest first
  const remainingToday = useMemo(() => {
    const now = new Date();
    return todayMeetings
      .filter(
        (apt) => new Date(apt.startTime) > now && apt.status !== "cancelled",
      )
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
  }, [todayMeetings]);

  // Counters: rest of the current week (tomorrow → Saturday) and the next
  // calendar week (Sunday → Saturday). upcomingAppointments starts tomorrow.
  const { restOfWeekCount, nextWeekCount } = useMemo(() => {
    const endOfWeek = new Date();
    endOfWeek.setDate(endOfWeek.getDate() + (6 - endOfWeek.getDay()));
    endOfWeek.setHours(23, 59, 59, 999);

    const endOfNextWeek = new Date(endOfWeek);
    endOfNextWeek.setDate(endOfNextWeek.getDate() + 7);

    let rest = 0;
    let next = 0;
    upcomingAppointments.forEach((apt) => {
      if (apt.status === "cancelled" || apt.isBlockedTime === true) return;
      const t = new Date(apt.startTime);
      if (t <= endOfWeek) rest += 1;
      else if (t <= endOfNextWeek) next += 1;
    });
    return { restOfWeekCount: rest, nextWeekCount: next };
  }, [upcomingAppointments]);

  return (
    <div
      className={`calendar-summary-overlay ${isCollapsed ? "collapsed" : ""}`}
    >
      <button
        className="cso-collapse-btn"
        onClick={() => setIsCollapsed((prev) => !prev)}
        aria-label={isCollapsed ? "הרחב סיכום יומי" : "מזער סיכום יומי"}
      >
        {isCollapsed ? (
          <ArrowsOutSimple size={14} weight="bold" />
        ) : (
          <ArrowsInSimple size={14} weight="bold" />
        )}
      </button>

      <div className="cso-main-row">
        {/* Today */}
        <div className="cso-section-label">
          <CalendarCheck size={15} weight="fill" />
          <span>היום</span>
          <span className="cso-count">{todayMeetings.length}</span>
        </div>

        {!isCollapsed && (
          <div className="cso-meetings">
            {remainingToday.length === 0 ? (
              <span className="cso-empty">אין פגישות נוספות להיום</span>
            ) : (
              remainingToday.map((apt) => (
                <button
                  key={apt.id}
                  className="cso-meeting"
                  onClick={() => onAppointmentClick(apt)}
                  title={apt.patientName}
                >
                  <span
                    className="cso-meeting-dot"
                    style={{ background: dotColor(apt) }}
                  />
                  <span className="cso-meeting-time">
                    {formatTime(apt.startTime)}
                  </span>
                  <span className="cso-meeting-name">{apt.patientName}</span>
                </button>
              ))
            )}
          </div>
        )}

        <div className="cso-divider" />

        {/* Week counters — numbers only */}
        <div className="cso-section-label">
          <span>השבוע</span>
          <span className="cso-count">{restOfWeekCount}</span>
        </div>
        <div className="cso-section-label cso-muted">
          <span>שבוע הבא</span>
          <span className="cso-count">{nextWeekCount}</span>
        </div>
      </div>

      {/* One-line color legend */}
      {!isCollapsed && (
        <div className="cso-legend">
          {legendItems.map((item, i) => (
            <span key={`${item.label}-${i}`} className="cso-legend-item">
              {i > 0 && <span className="cso-legend-sep">|</span>}
              <span
                className="cso-legend-dot"
                style={{ background: item.color }}
              />
              <span>{item.label}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default CalendarSummaryOverlay;
